import React, { useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import { UploadFile } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import PreviewCard from '../cards/PreviewCard';
import { Game, UploadType, UploadTypeNames } from '../../../workers/ApiWorker';
import {
    PreviewCardSizeArenas,
    PreviewCardSizeOthers,
} from '../../../helpers/UploadCardSizings';
import { useCustomTheme } from '../../../contexts/ThemeContext';
import {
    acceptedTypesArray,
    acceptedTypesString,
} from '../../../helpers/Images';

interface MultipleImageUploadsProps {
    handleImagesUpload: (files: File[]) => void;
    uploadData: {
        title: string;
        description: string;
        type: UploadType | undefined;
        games: Game[];
    };
    files?: File[];
    specificText?: string;
    disabled?: boolean;
}

const MultipleImageUploads: React.FC<MultipleImageUploadsProps> = ({
    handleImagesUpload,
    files,
    uploadData,
    specificText,
    disabled,
}) => {
    const { theme } = useCustomTheme();
    const [selectedImages, setSelectedImages] = useState<File[]>(files ?? []);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (
        event: React.DragEvent<HTMLDivElement>,
        index: number
    ) => {
        event.preventDefault();
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (targetIndex: number) => {
        if (draggedIndex === null || draggedIndex === targetIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const next = [...selectedImages];
        const [moved] = next.splice(draggedIndex, 1);
        next.splice(targetIndex, 0, moved);

        setSelectedImages(next);
        handleImagesUpload(next);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = event.target.files;
        if (fileList && fileList.length > 0) {
            const valid = Array.from(fileList).filter((file) =>
                acceptedTypesArray.includes(file.type)
            );

            if (valid.length > 0) {
                handleImagesUpload(valid);
                setSelectedImages(valid);
            }
        }

        event.target.value = '';
    };

    const clearImages = () => {
        setSelectedImages([]);
        handleImagesUpload([]);

        const fileInput = document.getElementById(
            'multipleImageUploadInput'
        ) as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleCardClick = () => {
        if (selectedImages.length === 0) {
            document.getElementById('multipleImageUploadInput')?.click();
        }
    };

    return (
        <Box>
            {selectedImages.length > 0 ? (
                uploadData.type ? (
                    <Card
                        sx={{
                            backgroundColor: 'transparent',
                            border: `2px solid ${theme.palette.primary.main}`,
                            position: 'relative',
                        }}
                        onClick={handleCardClick}
                    >
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: theme.palette.text.primary,
                                zIndex: 10,
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                            disabled={disabled}
                            onClick={(e) => {
                                e.stopPropagation();
                                clearImages();
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            marginTop={5}
                            marginBottom={5}
                            padding={1}
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    ...(uploadData.type &&
                                    uploadData.type.name ===
                                        UploadTypeNames.ARENA
                                        ? PreviewCardSizeArenas
                                        : PreviewCardSizeOthers),
                                }}
                            >
                                <PreviewCard
                                    title={uploadData.title}
                                    description={uploadData.description}
                                    image={URL.createObjectURL(
                                        selectedImages[0]
                                    )}
                                    galleryImages={selectedImages
                                        .slice(1)
                                        .map((file) =>
                                            URL.createObjectURL(file)
                                        )}
                                    games={uploadData.games}
                                    type={uploadData.type!}
                                />
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: 1,
                                paddingX: 2,
                                paddingBottom: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    width: '100%',
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                    marginBottom: 1,
                                }}
                            >
                                Drag the thumbnails to reorder
                            </Typography>
                            {selectedImages.map((file, index) => (
                                <Box
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                    draggable={!disabled}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={(e) =>
                                        handleDragOver(e, index)
                                    }
                                    onDrop={() => handleDrop(index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: `2px solid ${
                                            dragOverIndex === index &&
                                            draggedIndex !== index
                                                ? theme.palette.warning.main
                                                : theme.palette.primary.main
                                        }`,
                                        cursor: disabled ? 'default' : 'grab',
                                        opacity:
                                            draggedIndex === index ? 0.4 : 1,
                                        transition: 'opacity 0.15s, border 0.15s',
                                        '&:active': {
                                            cursor: disabled
                                                ? 'default'
                                                : 'grabbing',
                                        },
                                    }}
                                >
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={`thumbnail-${index}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Card>
                ) : (
                    <Card
                        sx={{
                            backgroundColor: 'transparent',
                            border: `2px solid ${theme.palette.primary.main}`,
                            position: 'relative',
                        }}
                        onClick={handleCardClick}
                    >
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: theme.palette.text.primary,
                                zIndex: 10,
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                            disabled={disabled}
                            onClick={(e) => {
                                e.stopPropagation();
                                clearImages();
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            marginTop={5}
                            marginBottom={5}
                            padding={1}
                            spacing={2}
                        >
                            <img
                                src={URL.createObjectURL(selectedImages[0])}
                                alt="test"
                                style={{
                                    height: '210px',
                                }}
                            />
                        </Grid>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                gap: 1,
                                paddingX: 2,
                                paddingBottom: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    width: '100%',
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                    marginBottom: 1,
                                }}
                            >
                                Drag the thumbnails to reorder
                            </Typography>
                            {selectedImages.map((file, index) => (
                                <Box
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                    draggable={!disabled}
                                    onDragStart={() => handleDragStart(index)}
                                    onDragOver={(e) =>
                                        handleDragOver(e, index)
                                    }
                                    onDrop={() => handleDrop(index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: `2px solid ${
                                            dragOverIndex === index &&
                                            draggedIndex !== index
                                                ? theme.palette.warning.main
                                                : theme.palette.primary.main
                                        }`,
                                        cursor: disabled ? 'default' : 'grab',
                                        opacity:
                                            draggedIndex === index ? 0.4 : 1,
                                        transition: 'opacity 0.15s, border 0.15s',
                                        '&:active': {
                                            cursor: disabled
                                                ? 'default'
                                                : 'grabbing',
                                        },
                                    }}
                                >
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={`thumbnail-${index}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Card>
                )
            ) : (
                <Card
                    sx={{
                        cursor:
                            selectedImages.length > 0 ? 'default' : 'pointer',
                        transition: 'transform 0.2s',
                        backgroundColor: 'transparent',
                        border: `2px solid ${theme.palette.primary.main}`,
                        padding: '20px',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        position: 'relative',
                    }}
                    onClick={handleCardClick}
                >
                    <UploadFile
                        sx={{
                            fontSize: '164px',
                            color: theme.palette.primary.main,
                            marginBottom: '10px',
                            marginTop: '40px',
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            marginTop: '10px',
                            color: theme.palette.text.secondary,
                            marginBottom: '40px',
                        }}
                    >
                        {specificText
                            ? specificText
                            : 'You have the bility to select multiple images which serve as the thumbnails for your submission\n' +
                              '                        and as the backgrounds for its page. '}
                        <br />
                        <br />
                        Supported file types include:
                        <br />
                        PNG
                        <br />
                        JPEG
                        <br />
                        WEBP
                    </Typography>
                </Card>
            )}
            <input
                id="multipleImageUploadInput"
                type="file"
                accept={acceptedTypesString}
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </Box>
    );
};

export default MultipleImageUploads;